import { MatrixEvent } from 'matrix-js-sdk/src/matrix';

import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

/**
 * Pure helpers for synthetic meeting-start backfill (FR-4).
 *
 * Problem: scheduled meetings can start and end while no messenger client has the chat
 * open. When the user opens the chat later, only a `MEETING_ENDED` is on the homeserver
 * timeline — leaving them with an end row and no idea what started or ended. These
 * helpers detect end events without a matching prior start (same `meeting_room_id`)
 * and produce a synthetic start row that renders next to the real end row.
 *
 * Async metadata resolution (meeting name / instant-vs-scheduled inference) lives in
 * `useMissingStartsResolver`. These functions are sync and side-effect-free so they are
 * trivial to unit-test.
 */

export interface ResolvedSyntheticStart {
  /** Backend `room_name` when the meeting is scheduled. Empty when instant / unknown. */
  meetingName: string;
  /** `started_by` Matrix user id from a paired start. Unknown for pure end-only timelines. */
  startedByName?: string;
  /** True when no backend row matched — render generic "Video meeting started". */
  isInstant: boolean;
}

export interface SyntheticMeetingStart {
  type: 'meeting-started-synthetic';
  key: string;
  /** Sort timestamp — guaranteed to fall before the corresponding end. */
  timestamp: number;
  meetingRoomId: string;
  /** Resolver may still be in flight on first render — `null` triggers generic copy. */
  resolved: ResolvedSyntheticStart | null;
}

export interface MissingStartPair {
  endEvent: MatrixEvent;
  meetingRoomId: string;
}

const MEETING_ENDED_TYPE = CustomMeedioMatrixEvents.MEETING_ENDED;
const MEETING_STARTED_TYPE = CustomMeedioMatrixEvents.MEETING_STARTED;

/**
 * Walk the timeline once. For each `MEETING_ENDED`, check whether a `MEETING_STARTED`
 * for the same `meeting_room_id` exists at or before its timestamp. Return the ends
 * that have no matching start — those are the rows that need a synthetic.
 */
export const findMissingStartPairs = (events: MatrixEvent[]): MissingStartPair[] => {
  const latestStartTsByRoom = new Map<string, number>();
  for (const event of events) {
    if (event.getType() !== MEETING_STARTED_TYPE) continue;
    const meetingRoomId = (event.getContent() ?? {}).meeting_room_id as string | undefined;
    if (!meetingRoomId) continue;
    const ts = event.getTs();
    const prev = latestStartTsByRoom.get(meetingRoomId);
    if (prev === undefined || ts > prev) latestStartTsByRoom.set(meetingRoomId, ts);
  }

  const pairs: MissingStartPair[] = [];
  const seenEnds = new Set<string>();
  for (const event of events) {
    if (event.getType() !== MEETING_ENDED_TYPE) continue;
    const meetingRoomId = (event.getContent() ?? {}).meeting_room_id as string | undefined;
    if (!meetingRoomId) continue;

    // Only synthesise one start per meeting_room_id — the earliest unmatched end is the
    // anchor. Later ends for the same room are either deduped or paired with a real start.
    if (seenEnds.has(meetingRoomId)) continue;
    seenEnds.add(meetingRoomId);

    const endTs = event.getTs();
    const startedTs = latestStartTsByRoom.get(meetingRoomId);
    if (startedTs !== undefined && startedTs <= endTs) continue;

    pairs.push({ endEvent: event, meetingRoomId });
  }

  return pairs;
};

/**
 * BUG-079: derive the synthetic-start label directly from the paired `MEETING_ENDED`
 * content. A scheduled meeting's end event carries `meeting_id` (the BUG-003 scheduled
 * discriminant), `meeting_name`, and `is_scheduled` — all identical on every client
 * because they are set by the emitter, not fetched per-client. Resolving the label here
 * makes the synthetic start render the same named "scheduled meeting has started" copy
 * for every participant and removes the flicker caused by the async, permission-scoped
 * `getMeetingByRoomId` resolver (which returned 403/404 → generic copy for non-creators).
 *
 * Returns `null` when the end event lacks this metadata (instant calls, or legacy ends
 * predating these fields) so the caller can fall back to the async resolver.
 */
export const resolveSyntheticStartFromEnd = (endEvent: MatrixEvent): ResolvedSyntheticStart | null => {
  const content = endEvent.getContent?.() ?? {};
  const meetingId = content.meeting_id;
  const hasMeetingId = meetingId !== undefined && meetingId !== null && meetingId !== '';
  const isScheduled = content.is_scheduled === true;
  const meetingName = typeof content.meeting_name === 'string' ? content.meeting_name : '';

  if (meetingName && (hasMeetingId || isScheduled)) {
    return { meetingName, isInstant: false };
  }

  return null;
};

/**
 * Build a synthetic timeline item from an end event + resolved metadata. The timestamp
 * is set to `end.ts − max(duration*1000, 1)` so the synthetic always sorts before the
 * end during merge. Duration is in seconds; we floor to 1ms minimum so the synthetic
 * never collides with the end event's timestamp.
 */
export const buildSyntheticStartItem = ({
  endEvent,
  meetingRoomId,
  resolved,
}: {
  endEvent: MatrixEvent;
  meetingRoomId: string;
  resolved: ResolvedSyntheticStart | null;
}): SyntheticMeetingStart => {
  const endTs = endEvent.getTs();
  const durationSec = (endEvent.getContent() ?? {}).duration as number | undefined;
  const offsetMs = typeof durationSec === 'number' && durationSec > 0 ? durationSec * 1000 : 1;
  const timestamp = Math.max(0, endTs - offsetMs);
  const endEventId = endEvent.getId() ?? `${meetingRoomId}-${endTs}`;
  return {
    type: 'meeting-started-synthetic',
    key: `synthetic-start-${endEventId}`,
    timestamp,
    meetingRoomId,
    resolved,
  };
};

/**
 * Top-level injection: read the timeline events + a `resolverCache` already populated
 * by `useMissingStartsResolver`, return the synthetic items that should appear next to
 * each unmatched end. `null` cache entries mean "still resolving" — the consumer renders
 * the generic placeholder copy until the cache settles.
 */
export const injectSyntheticMeetingStarts = ({
  events,
  resolverCache,
}: {
  events: MatrixEvent[];
  resolverCache: Map<string, ResolvedSyntheticStart | null>;
}): SyntheticMeetingStart[] => {
  const pairs = findMissingStartPairs(events);
  return pairs.map(({ endEvent, meetingRoomId }) => {
    // Prefer the label derived synchronously from the end content (consistent across
    // clients, no flicker); only fall back to the async resolver cache for ends that
    // lack the metadata (instant calls or legacy timelines).
    const resolved = resolveSyntheticStartFromEnd(endEvent) ?? resolverCache.get(meetingRoomId) ?? null;
    return buildSyntheticStartItem({ endEvent, meetingRoomId, resolved });
  });
};
